import Constants from 'expo-constants';
import { Platform } from 'react-native';

const LOCAL_API_PORT = '3001';
const LOCAL_WEB_FALLBACK = `http://localhost:${LOCAL_API_PORT}`;

function trimTrailingSlash(value: string) {
  return value.replace(/\/+$/, '');
}

function getExpoHost() {
  const hostUri = Constants.expoConfig?.hostUri;
  if (!hostUri) {
    return null;
  }

  return hostUri.replace(/:\d+$/, '');
}

function normalizeConfiguredBaseURL(
  configured: string,
  options: { rewriteLoopbackForNative?: boolean } = {},
) {
  try {
    const url = new URL(configured);
    const expoHost = getExpoHost();
    const isLoopback =
      url.hostname === 'localhost' || url.hostname === '127.0.0.1';
    const isExpoHostLoopback =
      expoHost === 'localhost' ||
      expoHost === '127.0.0.1' ||
      expoHost === '::1';

    if (
      options.rewriteLoopbackForNative &&
      Platform.OS !== 'web' &&
      isLoopback &&
      expoHost &&
      !isExpoHostLoopback
    ) {
      url.hostname = expoHost;
    }

    return trimTrailingSlash(url.toString());
  } catch {
    return trimTrailingSlash(configured);
  }
}

function stripAuthPath(value: string) {
  return value.replace(/\/api\/auth\/?$/, '');
}

function getHostedWebAuthOrigin() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return null;
  }

  try {
    const origin = window.location.origin;
    const { hostname } = new URL(origin);

    if (hostname === 'app.tabbio.com') {
      return trimTrailingSlash(origin);
    }
  } catch {
    return null;
  }

  return null;
}

// Same-origin must win over EXPO_PUBLIC_API_URL on hosted web so the Vercel
// `/api/*` rewrite handles cross-origin to server.tabbio.com instead of the
// browser, avoiding CORS preflight on restrictive networks.
function getHostedWebApiOrigin() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return null;
  }

  try {
    const origin = window.location.origin;
    const { hostname } = new URL(origin);

    if (hostname === 'app.tabbio.com' || hostname.endsWith('.tabbio.com')) {
      return trimTrailingSlash(origin);
    }
  } catch {
    return null;
  }

  return null;
}

export function getAPIBaseURL() {
  const hostedOrigin = getHostedWebApiOrigin();
  if (hostedOrigin) {
    return hostedOrigin;
  }

  const configuredEnv = process.env.EXPO_PUBLIC_API_URL;
  const configured = configuredEnv ? configuredEnv.trim() : '';

  if (configured) {
    return normalizeConfiguredBaseURL(configured, {
      rewriteLoopbackForNative: true,
    });
  }

  const expoHost = getExpoHost();
  if (Platform.OS !== 'web' && expoHost) {
    return `http://${expoHost}:${LOCAL_API_PORT}`;
  }

  return LOCAL_WEB_FALLBACK;
}

export function getSocketAPIBaseURL() {
  const configuredSocketEnv = process.env.EXPO_PUBLIC_SOCKET_URL;
  const configuredSocket = configuredSocketEnv ? configuredSocketEnv.trim() : '';

  if (configuredSocket) {
    return normalizeConfiguredBaseURL(configuredSocket, {
      rewriteLoopbackForNative: true,
    });
  }

  const configuredApiEnv = process.env.EXPO_PUBLIC_API_URL;
  const configuredApi = configuredApiEnv ? configuredApiEnv.trim() : '';

  if (configuredApi) {
    return normalizeConfiguredBaseURL(configuredApi, {
      rewriteLoopbackForNative: true,
    });
  }

  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    try {
      const { hostname } = new URL(window.location.origin);

      if (hostname === 'app.tabbio.com') {
        return 'https://server.tabbio.com';
      }
    } catch {
      // Fall back to the normal API base below.
    }
  }

  return getAPIBaseURL();
}

export function getAuthAPIBaseURL() {
  const hostedWebOrigin = getHostedWebAuthOrigin();
  if (hostedWebOrigin) {
    return hostedWebOrigin;
  }

  const configuredAuthEnv = process.env.EXPO_PUBLIC_AUTH_URL;
  const configuredAuth = configuredAuthEnv ? configuredAuthEnv.trim() : '';

  if (configuredAuth) {
    return stripAuthPath(normalizeConfiguredBaseURL(configuredAuth));
  }

  const configuredApiEnv = process.env.EXPO_PUBLIC_API_URL;
  const configuredApi = configuredApiEnv ? configuredApiEnv.trim() : '';

  if (configuredApi) {
    return stripAuthPath(normalizeConfiguredBaseURL(configuredApi));
  }

  if (Platform.OS !== 'web') {
    const expoHost = getExpoHost();
    if (expoHost) {
      return `http://${expoHost}:${LOCAL_API_PORT}`;
    }
    return LOCAL_WEB_FALLBACK;
  }

  return stripAuthPath(getAPIBaseURL());
}
